import * as React from 'react';

// Define a local interface for Crypto to satisfy TypeScript if 'randomUUID' is missing in the env types
interface CryptoWithUUID {
  randomUUID: () => string;
}

/** Button action rendered inside a toast notification. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

/** Data model for a single toast notification including history persistence fields. */
export interface ToastData {
  id: string;
  title: string;
  subtitle: string;
  // Explicitly allow undefined for exactOptionalPropertyTypes compatibility
  icon?: string | React.ReactNode | undefined;
  type?: 'success' | 'error' | 'info' | undefined;
  action?: ToastAction | undefined;
  secondaryAction?: ToastAction | undefined;
  timestamp: number; // Unix timestamp in milliseconds
  read?: boolean; // For notification history
}

interface ToastContextType {
  addToast: (toast: Omit<ToastData, 'id' | 'timestamp'>) => void;
  removeToast: (id: string) => void;
  toasts: ToastData[];
  notificationHistory: ToastData[];
  deleteNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  clearOldNotifications: () => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

const NOTIFICATION_HISTORY_KEY = 'decho_notification_history';
const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

/** Provides toast notifications with auto-dismiss, localStorage-backed history, and 3-day retention. */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = React.useState<ToastData[]>([]);
  const [notificationHistory, setNotificationHistory] = React.useState<ToastData[]>([]);

  // Load notification history from localStorage on mount
  React.useEffect(() => {
    try {
      const stored = localStorage.getItem(NOTIFICATION_HISTORY_KEY);
      if (stored) {
        const parsed: ToastData[] = JSON.parse(stored);
        // Filter out notifications older than 3 days
        const now = Date.now();
        const filtered = parsed.filter((notif) => now - notif.timestamp <= THREE_DAYS_MS);
        setNotificationHistory(filtered);
        // Update localStorage if we filtered anything out
        if (filtered.length !== parsed.length) {
          localStorage.setItem(NOTIFICATION_HISTORY_KEY, JSON.stringify(filtered));
        }
      }
    } catch {
      // Corrupt or unavailable localStorage — start fresh
    }
  }, []);

  // Save notification history to localStorage whenever it changes
  const saveNotificationHistory = React.useCallback((history: ToastData[]) => {
    try {
      // Remove icon property before saving (React nodes can't be serialized)
      const serializable = history.map(
        ({ icon: _icon, action: _action, secondaryAction: _secondaryAction, ...rest }) => rest
      );
      localStorage.setItem(NOTIFICATION_HISTORY_KEY, JSON.stringify(serializable));
    } catch {
      // localStorage may be full or unavailable — ignore
    }
  }, []);

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const deleteNotification = React.useCallback(
    (id: string) => {
      setNotificationHistory((prev) => {
        const updated = prev.filter((notif) => notif.id !== id);
        saveNotificationHistory(updated);
        return updated;
      });
    },
    [saveNotificationHistory]
  );

  const markAsRead = React.useCallback(
    (id: string) => {
      setNotificationHistory((prev) => {
        const updated = prev.map((notif) => (notif.id === id ? { ...notif, read: true } : notif));
        saveNotificationHistory(updated);
        return updated;
      });
    },
    [saveNotificationHistory]
  );

  const clearOldNotifications = React.useCallback(() => {
    const now = Date.now();
    setNotificationHistory((prev) => {
      const filtered = prev.filter((notif) => now - notif.timestamp <= THREE_DAYS_MS);
      saveNotificationHistory(filtered);
      return filtered;
    });
  }, [saveNotificationHistory]);

  const addToast = React.useCallback(
    ({
      title,
      subtitle,
      icon,
      type = 'success',
      action,
      secondaryAction,
    }: Omit<ToastData, 'id' | 'timestamp'>) => {
      // Securely generate UUID with fallback
      let id: string;
      if (
        typeof crypto !== 'undefined' &&
        typeof (crypto as unknown as CryptoWithUUID).randomUUID === 'function'
      ) {
        id = (crypto as unknown as CryptoWithUUID).randomUUID();
      } else {
        // Fallback for older environments
        id = Math.random().toString(36).slice(2, 11);
      }

      const timestamp = Date.now();
      const newToast: ToastData = {
        id,
        title,
        subtitle,
        type,
        timestamp,
        read: false,
        // Only include properties if they are defined to be safe,
        // though the type definition update now allows undefined.
        ...(icon !== undefined ? { icon } : {}),
        ...(action ? { action } : {}),
        ...(secondaryAction ? { secondaryAction } : {}),
      };
      setToasts((prev) => [...prev, newToast]);

      // Add to notification history
      setNotificationHistory((prev) => {
        const updated = [newToast, ...prev];
        saveNotificationHistory(updated);
        return updated;
      });

      // Auto-dismiss only for passive toasts (no primary action)
      if (!action) {
        setTimeout(() => {
          removeToast(id);
        }, 5000);
      }
    },
    [removeToast, saveNotificationHistory]
  );

  const value = React.useMemo(
    () => ({
      addToast,
      removeToast,
      toasts,
      notificationHistory,
      deleteNotification,
      markAsRead,
      clearOldNotifications,
    }),
    [
      addToast,
      removeToast,
      toasts,
      notificationHistory,
      deleteNotification,
      markAsRead,
      clearOldNotifications,
    ]
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

/** Accesses the toast notification context. Must be used within a {@link ToastProvider}. */
// eslint-disable-next-line react-refresh/only-export-components
export const useToast = (): ToastContextType => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
